import React, { useCallback, useEffect, useState } from "react";
import { Alert, Form, Input, InputNumber, Select, Switch } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormPage } from "../../components/Common";
import { useConfirm } from "../../hooks/useConfirm";
import { useMutations } from "../../hooks/useApiMutation";

/**
 * Full-page create/edit form for packages. The record to edit arrives via
 * navigation state from the list; a deep link without it falls back to the list.
 */
const PackageFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = location.state?.record ?? null;
  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const [euroPerToken, setEuroPerToken] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();

  // Watched so the overdraft explainer can state the actual terms being configured rather
  // than describing the feature in the abstract.
  const overdraftLimit = Form.useWatch("overdraftTokenLimit", form) || 0;
  const overdraftGraceDays = Form.useWatch("overdraftGraceDays", form) || 0;
  const overdraftEnabled = overdraftLimit > 0 && overdraftGraceDays > 0;
  const billingMode = Form.useWatch("billingMode", form) || "Tokens";

  const calculatePreview = useCallback(
    (values) => {
      const tokenCount = Number(values.tokenCount || 0);
      const topUpTokenCount = Number(values.topUpTokenCount || tokenCount || 0);
      return {
        totalPayment: Number((tokenCount * euroPerToken).toFixed(2)),
        topUpPrice: Number((topUpTokenCount * euroPerToken).toFixed(2)),
      };
    },
    [euroPerToken]
  );

  useEffect(() => {
    // Edit reached without the record (refresh / pasted link) — back to the list.
    if (isEdit && !editing) {
      navigate("/packages", { replace: true });
      return;
    }

    mutate("latestTokenValue", {
      method: "get",
      endpoint: "/tokenvalues/latest",
      onSuccess: (res) => setEuroPerToken(res?.euroPerToken || 0),
    });

    if (editing) {
      form.setFieldsValue({
        ...editing,
        currencyCode: editing.currencyCode || "EUR",
        billingMultiplier: editing.billingMultiplier ?? 1,
        billingMode: editing.billingMode || "Tokens",
        costMarkup: editing.costMarkup ?? 2.5,
        topUpTokenCount: editing.topUpTokenCount || editing.tokenCount,
        vatRate: editing.vatRate ?? 0,
        overdraftTokenLimit: editing.overdraftTokenLimit ?? 0,
        overdraftGraceDays: editing.overdraftGraceDays ?? 0,
      });
    } else {
      form.setFieldsValue({
        isActive: true,
        gracePeriodDays: 0,
        billingMultiplier: 1,
        billingMode: "Tokens",
        costMarkup: 2.5,
        currencyCode: "EUR",
        vatRate: 0,
        overdraftTokenLimit: 0,
        overdraftGraceDays: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    confirmAction({
      key: isEdit ? "updatePackage" : "createPackage",
      method: isEdit ? "put" : "post",
      endpoint: isEdit ? `/packages/${id}` : "/packages",
      payload: values,
      title: isEdit ? "Update Package" : "Create Package",
      content: "Submit package changes?",
      onSuccess: () => navigate("/packages"),
    });
    setSubmitting(false);
  };

  return (
    <FormPage
      title={isEdit ? "Edit Package" : "Create Package"}
      subtitle={
        isEdit
          ? `Editing "${editing?.name ?? ""}" — existing subscriptions keep their snapshotted terms.`
          : "Define a token bundle and its pricing. Prices are derived from the latest token value."
      }
      backTo="/packages"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitText={isEdit ? "Save Changes" : "Create Package"}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(_, allValues) => {
          form.setFieldsValue(calculatePreview(allValues));
        }}
      >
        <Form.Item name="packageId" hidden>
          <Input />
        </Form.Item>

        <div className="form-grid">
          <div className="form-section-title">General</div>

          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Business Standard" />
          </Form.Item>
          <Form.Item name="periodType" label="Period Type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "Monthly", label: "Monthly" },
                { value: "Annual", label: "Annual" },
              ]}
            />
          </Form.Item>
          <Form.Item name="description" label="Description" className="form-grid__full">
            <Input.TextArea rows={2} placeholder="Shown on invoices and the client portal" />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>

          <div className="form-section-title">Tokens & Pricing</div>

          <Form.Item name="tokenCount" label="Base Token Count" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>
          <Form.Item name="totalPayment" label="Base Price (auto)">
            <InputNumber style={{ width: "100%" }} disabled />
          </Form.Item>
          <Form.Item name="topUpTokenCount" label="Top-Up Token Count">
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>
          <Form.Item name="topUpPrice" label="Top-Up Price (auto)">
            <InputNumber style={{ width: "100%" }} disabled />
          </Form.Item>
          <Form.Item name="currencyCode" label="Currency" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "EUR", label: "EUR" },
                { value: "USD", label: "USD" },
              ]}
            />
          </Form.Item>
          <Form.Item name="billingMultiplier" label="Billing Multiplier" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0.1} step={0.1} />
          </Form.Item>
          <Form.Item
            name="billingMode"
            label="Billing Mode"
            rules={[{ required: true }]}
            tooltip="Tokens: output tokens × multiplier (legacy). Cost + markup: the real provider cost of every call × the markup — input-heavy calls such as invoice photos are priced by what they cost."
          >
            <Select
              options={[
                { value: "Tokens", label: "Tokens (output tokens × multiplier)" },
                { value: "CostPlus", label: "Cost + markup (provider cost × markup)" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="costMarkup"
            label="Cost Markup"
            rules={[{ required: billingMode === "CostPlus" }]}
            tooltip="Used only in cost + markup mode. 2.5 = the client pays 2.5 × what the provider bills us. Needs a token value (Token Values) so the amount can be converted to allowance tokens."
          >
            <InputNumber style={{ width: "100%" }} min={1} max={100} step={0.1} disabled={billingMode !== "CostPlus"} />
          </Form.Item>
          <Form.Item name="vatRate" label="VAT Rate (%)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} max={100} step={0.5} />
          </Form.Item>

          <div className="form-section-title">Limits</div>

          <Form.Item name="maxRequestsPerMinute" label="Max Requests/Minute">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="maxRequestsPerDay" label="Max Requests/Day">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item name="gracePeriodDays" label="Grace Period (Days)">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <div className="form-section-title">Overdraft</div>

          <Alert
            className="form-grid__full"
            type="info"
            showIcon
            message="Let clients keep working past their allowance, and pay for it after"
            description={
              overdraftEnabled
                ? `Once allowance and top-up credit are both spent, this plan keeps serving requests for up to ${Number(
                    overdraftLimit
                  ).toLocaleString()} tokens. The client then has ${overdraftGraceDays} day${
                    overdraftGraceDays === 1 ? "" : "s"
                  } to settle by buying a top-up — the purchase pays off the debt first, and only the remainder becomes spendable credit. Requests stop at whichever comes first: the token cap or the deadline.`
                : "Leave both at 0 to keep this plan strictly prepaid — requests stop the moment allowance and credit run out."
            }
            style={{ marginBottom: 4 }}
          />

          <Form.Item
            name="overdraftTokenLimit"
            label="Max Overdraft Tokens"
            tooltip="0 disables overdraft. Must be set together with the settlement period."
            rules={[
              {
                validator: (_, value) =>
                  Number(value || 0) > 0 && Number(form.getFieldValue("overdraftGraceDays") || 0) <= 0
                    ? Promise.reject(new Error("Set a settlement period too — a cap with no deadline never expires"))
                    : Promise.resolve(),
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={1000} />
          </Form.Item>
          <Form.Item
            name="overdraftGraceDays"
            label="Settlement Period (Days)"
            tooltip="How long the client has to pay off an overdraft before requests are refused."
            rules={[
              {
                validator: (_, value) =>
                  Number(value || 0) > 0 && Number(form.getFieldValue("overdraftTokenLimit") || 0) <= 0
                    ? Promise.reject(new Error("Set a token cap too — a deadline with no cap is unbounded"))
                    : Promise.resolve(),
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} max={365} />
          </Form.Item>
        </div>
      </Form>
    </FormPage>
  );
};

export default PackageFormPage;
